import Immutable from 'immutable';
import { createSelector, createSelectorCreator, defaultMemoize } from 'reselect';
import { equalityCheckNParamsCreator } from 'reselect-equality-check-n-parameters';
import { PerspectiveMercatorViewport } from 'viewport-mercator-project';
import { selectMap, selectTileJSON } from './base';

const createSelectorFirstParam = createSelectorCreator(equalityCheckNParamsCreator(1));
const createDeepEqualSelector = createSelectorCreator(
  defaultMemoize,
  (a, b) => Immutable.is(Immutable.fromJS(a), Immutable.fromJS(b)),
);
const roundZoom = zoom => Math.round(zoom * 1000) / 1000;

export const selectWidth = createSelector(selectMap, map => map.width);
export const selectHeight = createSelector(selectMap, map => map.height);

export const selectViewport = createSelector(
  [selectWidth, selectHeight],
  (width, height) => new PerspectiveMercatorViewport({ width, height, zoom: 0 }),
);

export const selectTileJSONBounds = createDeepEqualSelector(
  createSelector(selectTileJSON, json => json && json.bounds),
  (bounds) => {
    if (!bounds) return null;

    const [west, south, east, north] = bounds;
    return [[west, south], [east, north]];
  },
);

export const selectFlatTileJSONBounds = createSelector(
  selectTileJSONBounds,
  bounds => bounds && [...bounds[0], ...bounds[1]],
);

const padViewport = (bounds, viewport, padding = 100) => {
  try {
    return bounds
      ? viewport.fitBounds(bounds, { padding })
      : viewport;
  } catch (err) {
    return viewport;
  }
};

export const selectBoundViewport = createSelector(
  [selectTileJSONBounds, selectViewport],
  padViewport,
);

const getBounds = (viewport) => {
  const sw = viewport.unproject([0, viewport.height]);
  const ne = viewport.unproject([viewport.width, 0]);

  return [sw, ne];
};

export const selectMaxBounds = createSelector(selectBoundViewport, getBounds);

export const selectFitBounds = createSelectorFirstParam(
  selectViewport,
  createSelector(selectMap, map => map.bounds),
  selectMaxBounds,
  (_, bounds, maxBounds) => bounds || maxBounds,
);

export const selectZoom = createSelector(selectMap, map => map.zoom);
export const selectMinZoom = createSelector(
  selectBoundViewport,
  viewport => roundZoom(viewport.zoom),
);
export const selectRoundedZoomInteger = createSelector(
  selectZoom,
  zoom => Math.round(zoom),
);
export const selectRowLevelChangepoint = createSelector(
  [selectTileJSONBounds, selectViewport, () => -100],
  (bounds, viewport) => padViewport(bounds, viewport, -100).zoom,
);
export const selectShouldShowRows = createSelector(
  [selectRowLevelChangepoint, selectZoom],
  (changepoint, zoom) => zoom >= changepoint,
);

export const selectLocus = createSelector(selectTileJSON, json => json && json.locus);



// WEBPACK FOOTER //
// ./src/modules/map/selectors/viewport.js